import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Coins, TrendingUp, AlertTriangle, ChevronDown, ChevronRight, Edit3 } from 'lucide-react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { ComponentLifetime, TemplatesByType } from '../Förvaltning/TemplatesEditor/types';
import { Building as BuildingType, ValueWithMetadata, ConditionModel } from '../../types/protocols';
import { BuildingType as BuildingTypeEnum } from '../../types/BuildingTypeEnums';
import { COST_CATEGORY_COLORS, COST_CATEGORY_LABELS, COST_CATEGORIES, ALL_FINANCIAL_CATEGORIES } from '../../Helpers/chartColors';
import CustomMultiSelect, { CustomMultiSelectOption } from '../../Components/CustomMultiSelect';
import { YearlyActivity, CostCategory, MaintenancePlan, emptyCostsByCategory } from '../../Helpers/maintenanceCostUtils';
import OperatingCostModal from '../OperatingCosts/OperatingCostModal';
import { OperatingCost, CostInterval } from '../../types/operatingCosts';
import { operatingCostsApi } from '../../api/operatingCostsApi';
import { BudgetCard } from './components/BudgetCard';
import NumberInput from '../../Components/NumberInput';
import ToggleSwitch from '../../Components/ToggleSwitch';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';

interface SelectedOperatingCost {
    buildingKey: string;
    buildingName: string;
    cost: OperatingCost;
}

interface CostProjectionChartProps {
    yearlyPlans: YearlyActivity[];
    selectedCostCategories: CostCategory[];
    yearsToShow: number;
    adjustForInflation: boolean;
}

type ChartViewMode = 'category' | 'investment';

// Chart data types
interface ChartDataPoint {
    year: string;
    totalCost: number;
    activities: YearlyActivity['activities'];
    investment: number;
    operating: number;
    [category: string]: string | number | YearlyActivity['activities']; // Dynamic category keys
}

interface ActivityWithInflation {
    costCategory?: string;
    originalCost?: number;
    inflationYears?: number;
    inflationMultiplier?: number;
    cost: number;
}

// Pie chart data types
interface CategoryPieDataPoint {
    name: string;
    value: number;
    category: CostCategory;
}

interface InvestmentPieDataPoint {
    name: string;
    value: number;
    color: string;
}

// Activity type for operating cost callback
interface OperatingCostActivity {
    buildingKey?: string;
    buildingName?: string;
    operatingCostKey?: string;
}

// Building cost summary for year table
interface BuildingCostSummary {
    buildingKey: string;
    buildingName: string;
    costsByCategory: Record<CostCategory, number>;
    totalCost: number;
}

// Selected cell for activity details modal
interface SelectedCell {
    buildingKey: string;
    buildingName: string;
    category: CostCategory;
    activities: YearlyActivity['activities'];
}

interface YearBuildingSummaryTableProps {
    activities: YearlyActivity['activities'];
    selectedCostCategories: CostCategory[];
    showEmptyBuildings: boolean;
    onToggleShowEmpty: () => void;
    allBuildingKeys: string[];
    buildingSummaries: Record<string, { name?: string }>;
    onCellClick: (cell: SelectedCell) => void;
}

const YearBuildingSummaryTable: React.FC<YearBuildingSummaryTableProps> = React.memo(({
    activities,
    selectedCostCategories,
    showEmptyBuildings,
    onToggleShowEmpty,
    allBuildingKeys,
    buildingSummaries,
    onCellClick
}) => {
    // Group activities by building and category for lookup
    const activitiesByBuildingAndCategory = useMemo(() => {
        const map = new Map<string, YearlyActivity['activities']>();

        activities.forEach(activity => {
            if (!activity.buildingKey || !activity.costCategory) return;
            const key = `${activity.buildingKey}-${activity.costCategory}`;
            const existing = map.get(key) || [];
            existing.push(activity);
            map.set(key, existing);
        });

        return map;
    }, [activities]);

    const getActivitiesForCell = useCallback((buildingKey: string, category: CostCategory) => {
        return activitiesByBuildingAndCategory.get(`${buildingKey}-${category}`) || [];
    }, [activitiesByBuildingAndCategory]);

    // Group activities by building and sum costs by category
    const buildingCostSummaries = useMemo(() => {
        const summaryMap = new Map<string, BuildingCostSummary>();

        // Initialize all buildings if showing empty
        if (showEmptyBuildings) {
            allBuildingKeys.forEach(buildingKey => {
                const buildingSummary = buildingSummaries[buildingKey];
                summaryMap.set(buildingKey, {
                    buildingKey,
                    buildingName: buildingSummary?.name || 'Okänd byggnad',
                    costsByCategory: emptyCostsByCategory(),
                    totalCost: 0
                });
            });
        }

        // Sum up activities by building and category
        activities.forEach(activity => {
            if (!activity.buildingKey) return;

            let costSummary = summaryMap.get(activity.buildingKey);
            if (!costSummary) {
                costSummary = {
                    buildingKey: activity.buildingKey,
                    buildingName: activity.buildingName || 'Okänd byggnad',
                    costsByCategory: emptyCostsByCategory(),
                    totalCost: 0
                };
                summaryMap.set(activity.buildingKey, costSummary);
            }

            const category = activity.costCategory as CostCategory;
            if (category && selectedCostCategories.includes(category)) {
                costSummary.costsByCategory[category] += activity.cost;
                costSummary.totalCost += activity.cost;
            }
        });

        // Convert to array and filter/sort
        let summaries = Array.from(summaryMap.values());

        if (!showEmptyBuildings) {
            // Show buildings with any financial activity (costs or income)
            summaries = summaries.filter(s => s.totalCost !== 0);
        }

        // Sort by total cost descending (highest costs first, income last)
        summaries.sort((a, b) => b.totalCost - a.totalCost);

        return summaries;
    }, [activities, selectedCostCategories, showEmptyBuildings, allBuildingKeys, buildingSummaries]);

    // Calculate column totals
    const columnTotals = useMemo(() => {
        const totals: Record<CostCategory, number> = emptyCostsByCategory();
        let grandTotal = 0;

        buildingCostSummaries.forEach(costSummary => {
            selectedCostCategories.forEach(category => {
                totals[category] += costSummary.costsByCategory[category];
            });
            grandTotal += costSummary.totalCost;
        });

        return { ...totals, total: grandTotal };
    }, [buildingCostSummaries, selectedCostCategories]);

    // Count buildings with no financial activity (totalCost === 0)
    const emptyBuildingsCount = allBuildingKeys.length - buildingCostSummaries.filter(s => s.totalCost !== 0).length;

    if (buildingCostSummaries.length === 0 && !showEmptyBuildings) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                <p style={{ margin: 0 }}>Inga poster för detta år</p>
                {emptyBuildingsCount > 0 && (
                    <button
                        className="btn btn-link btn-sm"
                        onClick={onToggleShowEmpty}
                        style={{ marginTop: '8px' }}
                    >
                        Visa {emptyBuildingsCount} byggnader utan poster
                    </button>
                )}
            </div>
        );
    }

    return (
        <div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>
                            Byggnad
                        </th>
                        {selectedCostCategories.map(category => (
                            <th
                                key={category}
                                style={{
                                    padding: '12px 16px',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <span
                                    style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: COST_CATEGORY_COLORS[category],
                                        display: 'inline-block',
                                        marginRight: '6px'
                                    }}
                                />
                                {COST_CATEGORY_LABELS[category]}
                            </th>
                        ))}
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>
                            Totalt
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {buildingCostSummaries.map((summary, index) => (
                        <tr
                            key={summary.buildingKey}
                            style={{
                                borderBottom: '1px solid #e9ecef',
                                backgroundColor: index % 2 === 0 ? 'transparent' : '#f8f9fa',
                                // Dim buildings with no financial activity
                                opacity: summary.totalCost === 0 ? 0.5 : 1
                            }}
                        >
                            <td style={{ padding: '10px 16px' }}>
                                <a
                                    href={`/building/${summary.buildingKey}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#369ec3',
                                        textDecoration: 'none'
                                    }}
                                >
                                    {summary.buildingName}
                                </a>
                            </td>
                            {selectedCostCategories.map(category => {
                                const cost = summary.costsByCategory[category];
                                const hasActivities = cost !== 0;
                                const isIncome = category === 'rental-income';
                                // For rental income, show as positive number (it's stored negative)
                                const displayValue = isIncome ? Math.abs(cost) : cost;
                                return (
                                    <td
                                        key={category}
                                        style={{
                                            padding: '10px 16px',
                                            textAlign: 'right',
                                            color: hasActivities
                                                ? (isIncome ? '#059669' : '#369ec3')
                                                : '#adb5bd',
                                            cursor: hasActivities ? 'pointer' : 'default',
                                            textDecoration: hasActivities ? 'underline' : 'none'
                                        }}
                                        onClick={hasActivities ? () => onCellClick({
                                            buildingKey: summary.buildingKey,
                                            buildingName: summary.buildingName,
                                            category,
                                            activities: getActivitiesForCell(summary.buildingKey, category)
                                        }) : undefined}
                                    >
                                        {hasActivities
                                            ? `${Math.round(displayValue).toLocaleString('sv-SE')} kr`
                                            : '–'}
                                    </td>
                                );
                            })}
                            <td
                                style={{
                                    padding: '10px 16px',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                    // Green for negative (income > costs), red for positive costs
                                    color: summary.totalCost !== 0
                                        ? (summary.totalCost < 0 ? '#059669' : '#369ec3')
                                        : 'inherit',
                                    cursor: summary.totalCost !== 0 ? 'pointer' : 'default',
                                    textDecoration: summary.totalCost !== 0 ? 'underline' : 'none'
                                }}
                                onClick={summary.totalCost !== 0 ? () => {
                                    // Get all activities for this building across all selected categories
                                    const allActivitiesForBuilding = activities.filter(
                                        a => a.buildingKey === summary.buildingKey &&
                                             a.costCategory &&
                                             selectedCostCategories.includes(a.costCategory as CostCategory)
                                    );
                                    onCellClick({
                                        buildingKey: summary.buildingKey,
                                        buildingName: summary.buildingName,
                                        category: 'maintenance-utility' as CostCategory, // Will be ignored, we pass all activities
                                        activities: allActivitiesForBuilding
                                    });
                                } : undefined}
                            >
                                {summary.totalCost !== 0
                                    ? `${Math.round(summary.totalCost).toLocaleString('sv-SE')} kr`
                                    : '–'}
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr style={{ borderTop: '2px solid #dee2e6', backgroundColor: '#f1f3f4' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                            Summa
                        </td>
                        {selectedCostCategories.map(category => {
                            const total = columnTotals[category];
                            const isIncome = category === 'rental-income';
                            // For rental income, show as positive number
                            const displayValue = isIncome ? Math.abs(total) : total;
                            return (
                                <td
                                    key={category}
                                    style={{
                                        padding: '12px 16px',
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        color: isIncome && total !== 0 ? '#059669' : 'inherit'
                                    }}
                                >
                                    {total !== 0
                                        ? `${Math.round(displayValue).toLocaleString('sv-SE')} kr`
                                        : '–'}
                                </td>
                            );
                        })}
                        <td style={{
                            padding: '12px 16px',
                            textAlign: 'right',
                            fontWeight: 700,
                            color: columnTotals.total < 0 ? '#059669' : (columnTotals.total > 0 ? '#dc3545' : 'inherit')
                        }}>
                            {Math.round(columnTotals.total).toLocaleString('sv-SE')} kr
                        </td>
                    </tr>
                </tfoot>
            </table>

            {!showEmptyBuildings && emptyBuildingsCount > 0 && (
                <div style={{ padding: '12px 16px', textAlign: 'center', borderTop: '1px solid #e9ecef' }}>
                    <button
                        className="btn btn-link btn-sm"
                        onClick={onToggleShowEmpty}
                        style={{ color: '#6c757d' }}
                    >
                        Visa {emptyBuildingsCount} byggnader utan poster
                    </button>
                </div>
            )}

            {showEmptyBuildings && emptyBuildingsCount > 0 && (
                <div style={{ padding: '12px 16px', textAlign: 'center', borderTop: '1px solid #e9ecef' }}>
                    <button
                        className="btn btn-link btn-sm"
                        onClick={onToggleShowEmpty}
                        style={{ color: '#6c757d' }}
                    >
                        Dölj byggnader utan poster
                    </button>
                </div>
            )}
        </div>
    );
})

// Short axis labels for narrow charts: 850 tkr, 1,2 mkr.
const formatCompactKr = (value: number): string => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} mkr`;
    if (abs >= 1_000) return `${Math.round(value / 1_000).toLocaleString('sv-SE')} tkr`;
    return Math.round(value).toLocaleString('sv-SE');
};

const INVESTMENT_BAR_COLOR = '#4a90d9';
const OPERATING_BAR_COLOR = '#82ca9d';

const CostProjectionChart: React.FC<CostProjectionChartProps> = React.memo(({
    yearlyPlans,
    selectedCostCategories,
    yearsToShow,
    adjustForInflation
}) => {
    const [viewMode, setViewMode] = useState<ChartViewMode>('category');

    // On phones the bar chart takes the full card width above the pie, so it
    // drops the rotated axis title and wide margins and uses compact tick labels.
    const isSmallScreen = useIsSmallScreen();
    const barMargin = isSmallScreen
        ? { top: 10, right: 8, left: 0, bottom: 10 }
        : { top: 20, right: 30, left: 80, bottom: 60 };
    const yAxisProps = isSmallScreen
        ? { width: 64, tickFormatter: formatCompactKr }
        : {
            label: { value: 'Kostnad (kr)', angle: -90, position: 'insideLeft' as const, offset: -15 },
            tickFormatter: (value: number) => Math.round(value).toLocaleString('sv-SE'),
        };
    const xAxisProps = isSmallScreen
        ? { height: 44, tickMargin: 10 }
        : { height: 60, tickMargin: 20 };
    const legendWrapperStyle = isSmallScreen ? { fontSize: 12, lineHeight: '20px', paddingTop: 8 } : undefined;

    // Build chart data - always calculate both category and investment data
    const chartData = useMemo(() => {
        return yearlyPlans.map(plan => {
            const data: ChartDataPoint = {
                year: plan.year.toString(),
                totalCost: plan.totalCost,
                activities: plan.activities,
                investment: 0,
                operating: 0
            };

            // Always calculate costs by category
            const costsByCategory: Record<string, number> = {};
            plan.activities.forEach(activity => {
                if (activity.costCategory) {
                    costsByCategory[activity.costCategory] = (costsByCategory[activity.costCategory] || 0) + activity.cost;
                }
            });

            selectedCostCategories.forEach(category => {
                data[category] = costsByCategory[category] || 0;
            });

            // Always calculate investment vs operating
            let investmentCost = 0;
            let operatingCost = 0;

            plan.activities.forEach(activity => {
                const cost = activity.cost || 0;
                const investmentPct = (activity.investmentPercentage || 0) / 100;
                investmentCost += cost * investmentPct;
                operatingCost += cost * (1 - investmentPct);
            });

            data.investment = investmentCost;
            data.operating = operatingCost;

            return data;
        });
    }, [yearlyPlans, selectedCostCategories]);

    const tooltipFormatter = (value: number, name: string, props: { payload?: ChartDataPoint }) => {
        const payload = props.payload;

        if (viewMode === 'investment') {
            const label = name === 'investment' ? 'Investering' : 'Drift/underhåll';
            return [`${Math.round(value).toLocaleString('sv-SE')} kr`, label];
        }

        const categoryLabel = COST_CATEGORY_LABELS[name as CostCategory] || name;

        if (adjustForInflation && payload?.activities) {
            const relevantActivities = (payload.activities as ActivityWithInflation[]).filter(activity =>
                activity.costCategory === name && activity.originalCost && (activity.inflationYears ?? 0) > 0
            );

            if (relevantActivities.length > 0) {
                const totalOriginalCost = relevantActivities.reduce((sum, activity) =>
                    sum + (activity.originalCost ?? 0), 0
                );
                const avgInflationYears = relevantActivities.reduce((sum, activity) =>
                    sum + (activity.inflationYears ?? 0), 0
                ) / relevantActivities.length;
                const avgInflationPercent = relevantActivities.reduce((sum, activity) =>
                    sum + ((activity.inflationMultiplier ?? 1) - 1) * 100, 0
                ) / relevantActivities.length;

                return [
                    `${Math.round(value).toLocaleString('sv-SE')} kr\n(Ursprunglig: ${Math.round(totalOriginalCost).toLocaleString('sv-SE')} kr, +${Math.round(avgInflationPercent)}% över ${Math.round(avgInflationYears)} år)`,
                    categoryLabel
                ];
            }
        }
        return [`${Math.round(value).toLocaleString('sv-SE')} kr`, categoryLabel];
    };

    const legendFormatter = (value: string) => {
        if (viewMode === 'investment') {
            return value === 'investment' ? 'Investering' : 'Drift/underhåll';
        }
        return COST_CATEGORY_LABELS[value as CostCategory] || value;
    };

    // Calculate pie chart data for category view
    const categoryPieData = useMemo(() => {
        return selectedCostCategories.map(category => {
            const total = yearlyPlans.reduce((sum, plan) => {
                const categoryTotal = plan.activities
                    .filter(a => a.costCategory === category)
                    .reduce((s, a) => s + a.cost, 0);
                return sum + categoryTotal;
            }, 0);
            return {
                name: COST_CATEGORY_LABELS[category],
                value: total,
                category
            };
        }).filter(item => item.value > 0);
    }, [yearlyPlans, selectedCostCategories]);

    // Calculate pie chart data for investment view
    const investmentPieData = useMemo(() => {
        let investment = 0;
        let operating = 0;

        yearlyPlans.forEach(plan => {
            plan.activities
                .filter(a => a.costCategory && selectedCostCategories.includes(a.costCategory as CostCategory))
                .forEach(a => {
                    const investmentPct = (a.investmentPercentage || 0) / 100;
                    investment += a.cost * investmentPct;
                    operating += a.cost * (1 - investmentPct);
                });
        });

        return [
            { name: 'Investering', value: investment, color: INVESTMENT_BAR_COLOR },
            { name: 'Drift/underhåll', value: operating, color: OPERATING_BAR_COLOR }
        ].filter(item => item.value > 0);
    }, [yearlyPlans, selectedCostCategories]);

    const pieTotalCost = viewMode === 'category'
        ? categoryPieData.reduce((sum, item) => sum + item.value, 0)
        : investmentPieData.reduce((sum, item) => sum + item.value, 0);

    return (
        <div className="std-box dropshadow" style={{ height: '100%' }}>
            {/* ekonomi-chart-* classes: the generic .chart-* ones are also styled by
                the Förvaltning maintenance page and leak in globally. */}
            <div className="ekonomi-chart-header">
                <div className="ekonomi-chart-heading">
                    <h3 className="ekonomi-chart-title">
                        <TrendingUp size={20} className="me-2" aria-hidden="true" />
                        Aggregerad kostnadsprognos
                    </h3>
                    <p className="ekonomi-chart-subtitle">
                        Totala planerade kostnader för alla byggnader per år för de kommande {yearsToShow} åren
                    </p>
                </div>
                <select
                    className="form-select form-select-sm ekonomi-chart-mode"
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value as ChartViewMode)}
                    aria-label="Visa diagram per"
                >
                    <option value="category">Kostnadstyp</option>
                    <option value="investment">Investering</option>
                </select>
            </div>
            <div className="ekonomi-chart-body">
                {/* Bar Chart */}
                <div className="ekonomi-chart-bar">
                    {viewMode === 'category' ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={barMargin}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="year"
                                    angle={-45}
                                    textAnchor="end"
                                    interval="preserveStartEnd"
                                    {...xAxisProps}
                                />
                                <YAxis {...yAxisProps} />
                                <Tooltip
                                    formatter={tooltipFormatter}
                                    labelFormatter={(label) => `År ${label}`}
                                />
                                <Legend formatter={legendFormatter} wrapperStyle={legendWrapperStyle} />
                                {selectedCostCategories.map((category) => (
                                    <Bar
                                        key={category}
                                        dataKey={category}
                                        stackId="costs"
                                        fill={COST_CATEGORY_COLORS[category]}
                                        name={category}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={barMargin}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="year"
                                    angle={-45}
                                    textAnchor="end"
                                    interval="preserveStartEnd"
                                    {...xAxisProps}
                                />
                                <YAxis {...yAxisProps} />
                                <Tooltip
                                    formatter={tooltipFormatter}
                                    labelFormatter={(label) => `År ${label}`}
                                />
                                <Legend formatter={legendFormatter} wrapperStyle={legendWrapperStyle} />
                                <Bar
                                    dataKey="investment"
                                    stackId="investment"
                                    fill={INVESTMENT_BAR_COLOR}
                                    name="investment"
                                />
                                <Bar
                                    dataKey="operating"
                                    stackId="investment"
                                    fill={OPERATING_BAR_COLOR}
                                    name="operating"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* Pie Chart */}
                {pieTotalCost > 0 && (
                    <div className="ekonomi-chart-pie">
                        <div className="ekonomi-chart-pie-plot">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={viewMode === 'category' ? categoryPieData : investmentPieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={90}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {viewMode === 'category'
                                            ? categoryPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COST_CATEGORY_COLORS[entry.category]} />
                                            ))
                                            : investmentPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))
                                        }
                                    </Pie>
                                    <Tooltip
                                        formatter={(value: number, _name: string, props: { payload?: { name?: string } }) => {
                                            const percent = ((value / pieTotalCost) * 100).toFixed(0);
                                            return [
                                                `${Math.round(value).toLocaleString('sv-SE')} kr (${percent}%)`,
                                                props.payload?.name ?? ''
                                            ];
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="ekonomi-chart-pie-total">
                            Totalt: <strong>{Math.round(pieTotalCost).toLocaleString('sv-SE')} kr</strong>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

interface CostDistributionChartProps {
    yearlyPlans: YearlyActivity[];
    selectedCostCategories: CostCategory[];
    yearsToShow: number;
}

const CostDistributionChart: React.FC<CostDistributionChartProps> = React.memo(({
    yearlyPlans,
    selectedCostCategories,
    yearsToShow
}) => {
    // Calculate total costs by category across all years
    const totalsByCategory = useMemo(() => {
        return selectedCostCategories.map(category => {
            const total = yearlyPlans.reduce((sum, plan) => {
                const categoryTotal = plan.activities
                    .filter(a => a.costCategory === category)
                    .reduce((s, a) => s + a.cost, 0);
                return sum + categoryTotal;
            }, 0);
            return {
                name: COST_CATEGORY_LABELS[category],
                value: total,
                category
            };
        }).filter(item => item.value > 0);
    }, [yearlyPlans, selectedCostCategories]);

    const totalCost = useMemo(() =>
        totalsByCategory.reduce((sum, item) => sum + item.value, 0),
    [totalsByCategory]);

    if (totalCost === 0) return null;

    return (
        <div style={{ flex: 1, minWidth: '200px' }}>
            <h6 style={{ marginBottom: '8px', color: '#495057', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Coins size={16} />
                Kostnadsfördelning
            </h6>
            <div style={{ height: '200px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={totalsByCategory}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="value"
                        >
                            {totalsByCategory.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COST_CATEGORY_COLORS[entry.category]} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value: number, _name: string, props: { payload?: CategoryPieDataPoint }) => {
                                const percent = ((value / totalCost) * 100).toFixed(0);
                                return [
                                    `${Math.round(value).toLocaleString('sv-SE')} kr (${percent}%)`,
                                    props.payload?.name ?? ''
                                ];
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div style={{ textAlign: 'center', fontSize: '12px', color: '#666' }}>
                Totalt: <strong>{Math.round(totalCost).toLocaleString('sv-SE')} kr</strong>
            </div>
        </div>
    );
});

interface InvestmentVsOperatingChartProps {
    yearlyPlans: YearlyActivity[];
    selectedCostCategories: CostCategory[];
    yearsToShow: number;
}

const InvestmentVsOperatingChart: React.FC<InvestmentVsOperatingChartProps> = React.memo(({
    yearlyPlans,
    selectedCostCategories,
    yearsToShow
}) => {
    // Calculate investment vs operating costs
    // Investment cost = sum of (activity.cost * investmentPercentage / 100)
    // Operating cost = sum of (activity.cost * (1 - investmentPercentage / 100))
    // Default investmentPercentage is 0% (all cost is operating)
    const { investmentCost, operatingCost } = useMemo(() => {
        let investment = 0;
        let operating = 0;

        yearlyPlans.forEach(plan => {
            plan.activities
                .filter(a => a.costCategory && selectedCostCategories.includes(a.costCategory as CostCategory))
                .forEach(a => {
                    const investmentPct = (a.investmentPercentage || 0) / 100;
                    investment += a.cost * investmentPct;
                    operating += a.cost * (1 - investmentPct);
                });
        });

        return { investmentCost: investment, operatingCost: operating };
    }, [yearlyPlans, selectedCostCategories]);

    const totalCost = investmentCost + operatingCost;

    if (totalCost === 0) return null;

    const data = [
        { name: 'Investering', value: investmentCost, color: INVESTMENT_BAR_COLOR },
        { name: 'Drift/underhåll', value: operatingCost, color: OPERATING_BAR_COLOR }
    ].filter(item => item.value > 0);

    return (
        <div style={{ flex: 1, minWidth: '200px' }}>
            <h6 style={{ marginBottom: '8px', color: '#495057', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TrendingUp size={16} />
                Investering vs drift/underhåll
            </h6>
            <div style={{ height: '200px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="value"
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value: number, _name: string, props: { payload?: InvestmentPieDataPoint }) => {
                                const percent = ((value / totalCost) * 100).toFixed(0);
                                return [
                                    `${Math.round(value).toLocaleString('sv-SE')} kr (${percent}%)`,
                                    props.payload?.name ?? ''
                                ];
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div style={{ textAlign: 'center', fontSize: '12px', color: '#666' }}>
                Totalt: <strong>{Math.round(totalCost).toLocaleString('sv-SE')} kr</strong>
            </div>
        </div>
    );
});

interface BuildingSummary {
    name?: string;
    buildingType?: BuildingTypeEnum;
}

interface MaintenanceCostsTabProps {
    buildings: Record<string, BuildingType>;
    buildingSummaries: Record<string, BuildingSummary>;
    activePlans: Record<string, MaintenancePlan>;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    componentConditions: Record<string, Record<string, ValueWithMetadata<ConditionModel>>>;
    loading: boolean;
    yearsToShow: number;
    setYearsToShow: (years: number) => void;
    yearlyPlans: YearlyActivity[];
    selectedBuildingTypes: BuildingTypeEnum[];
    setSelectedBuildingTypes: (types: BuildingTypeEnum[]) => void;
    adjustForInflation: boolean;
    setAdjustForInflation: (adjust: boolean) => void;
    inflationRate: number;
    setInflationRate: (rate: number) => void;
    buildingTypeOptions: CustomMultiSelectOption[];
    selectedCostCategories: CostCategory[];
    setSelectedCostCategories: (categories: CostCategory[]) => void;
    onOperatingCostUpdated: () => void;
}

const MaintenanceCostsTab: React.FC<MaintenanceCostsTabProps> = ({
    buildings,
    buildingSummaries,
    activePlans,
    componentLifetimes,
    maintenanceTemplates,
    componentConditions,
    loading,
    yearsToShow,
    setYearsToShow,
    yearlyPlans,
    selectedBuildingTypes,
    setSelectedBuildingTypes,
    adjustForInflation,
    setAdjustForInflation,
    inflationRate,
    setInflationRate,
    buildingTypeOptions,
    selectedCostCategories,
    setSelectedCostCategories,
    onOperatingCostUpdated
}) => {
    // Operating cost edit modal state
    const [selectedOperatingCost, setSelectedOperatingCost] = useState<SelectedOperatingCost | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);

    // Cell activities modal state
    const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
    const [showCellModal, setShowCellModal] = useState(false);

    // Track which years are expanded (first year expanded by default)
    const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());

    // Track which years show buildings with no costs
    const [yearsShowingEmptyBuildings, setYearsShowingEmptyBuildings] = useState<Set<number>>(new Set());

    const toggleShowEmptyBuildings = useCallback((year: number) => {
        setYearsShowingEmptyBuildings(prev => {
            const next = new Set(prev);
            if (next.has(year)) {
                next.delete(year);
            } else {
                next.add(year);
            }
            return next;
        });
    }, []);

    // Get all building keys from buildingSummaries that match selected building types
    const allBuildingKeys = useMemo(() => {
        return Object.keys(buildingSummaries).filter(buildingKey => {
            const summary = buildingSummaries[buildingKey];
            return summary?.buildingType && selectedBuildingTypes.includes(summary.buildingType);
        });
    }, [buildingSummaries, selectedBuildingTypes]);

    // Filter yearly plans by selected cost categories
    const filteredYearlyPlans = useMemo(() => {
        if (selectedCostCategories.length === ALL_FINANCIAL_CATEGORIES.length) {
            // All categories selected, no filtering needed
            return yearlyPlans;
        }

        return yearlyPlans.map(yearPlan => {
            const filteredActivities = yearPlan.activities.filter(activity => {
                const category = activity.costCategory;
                return category && selectedCostCategories.includes(category);
            });

            // Recalculate costs by category
            const costsByCategory: Record<CostCategory, number> = emptyCostsByCategory();

            filteredActivities.forEach(activity => {
                if (activity.costCategory) {
                    costsByCategory[activity.costCategory] += activity.cost;
                }
            });

            const filteredTotalCost = filteredActivities.reduce((sum, a) => sum + a.cost, 0);

            // Also recalculate costsByBaseType for filtered activities
            const costsByBaseType: Record<string, number> = {};
            filteredActivities.forEach(activity => {
                costsByBaseType[activity.baseType] = (costsByBaseType[activity.baseType] || 0) + activity.cost;
            });

            return {
                ...yearPlan,
                activities: filteredActivities,
                totalCost: filteredTotalCost,
                costsByCategory,
                costsByBaseType
            };
        });
    }, [yearlyPlans, selectedCostCategories]);

    const totalActivities = filteredYearlyPlans.reduce((total, year) => total + year.activities.length, 0);
    const totalCost = filteredYearlyPlans.reduce((total, year) => total + year.totalCost, 0);

    // Get current year's unfiltered total cost for budget comparison
    const currentYear = new Date().getFullYear();
    const currentYearPlan = useMemo(
        () => yearlyPlans.find(plan => plan.year === currentYear),
        [yearlyPlans, currentYear]
    );
    const currentYearCost = currentYearPlan?.totalCost ?? 0;

    // Current-year cost broken down by category (unfiltered, to match currentYearCost),
    // derived from activities since costsByCategory is dropped by aggregateYearlyPlans.
    const currentYearCostByCategory = useMemo(() => {
        const totals = emptyCostsByCategory();
        currentYearPlan?.activities.forEach(activity => {
            if (activity.costCategory) {
                totals[activity.costCategory] += activity.cost;
            }
        });
        return totals;
    }, [currentYearPlan]);

    // Initialize expanded years - expand first year only (on first load)
    const firstYear = filteredYearlyPlans.length > 0 ? filteredYearlyPlans[0].year : null;
    const [initializedForFirstYear, setInitializedForFirstYear] = useState<number | null>(null);

    useEffect(() => {
        if (firstYear !== null && initializedForFirstYear !== firstYear) {
            setExpandedYears(new Set([firstYear]));
            setInitializedForFirstYear(firstYear);
        }
    }, [firstYear, initializedForFirstYear]);

    const toggleYearExpanded = useCallback((year: number) => {
        setExpandedYears(prev => {
            const next = new Set(prev);
            if (next.has(year)) {
                next.delete(year);
            } else {
                next.add(year);
            }
            return next;
        });
    }, []);

    // Count only buildings that match the selected building types and have active plans
    const activeBuildingsCount = Object.keys(activePlans).filter(buildingKey => {
        const building = buildings[buildingKey];
        return building?.summary?.buildingType && selectedBuildingTypes.includes(building.summary.buildingType);
    }).length;

    // Handle cell click - show activities modal
    const handleCellClick = useCallback((cell: SelectedCell) => {
        setSelectedCell(cell);
        setShowCellModal(true);
    }, []);

    // Close cell modal
    const handleCloseCellModal = useCallback(() => {
        setShowCellModal(false);
        setSelectedCell(null);
    }, []);

    // Handle operating cost click - show warning modal first
    const handleOperatingCostClick = useCallback(async (activity: OperatingCostActivity) => {
        if (!activity.buildingKey || !activity.operatingCostKey) {
            console.error('Missing buildingKey or operatingCostKey', activity);
            return;
        }

        // Fetch the full operating cost data
        try {
            const costs = await operatingCostsApi.getCosts(activity.buildingKey);
            const cost = costs.find(c => c.key === activity.operatingCostKey);

            if (cost) {
                setSelectedOperatingCost({
                    buildingKey: activity.buildingKey,
                    buildingName: activity.buildingName || 'Okänd byggnad',
                    cost
                });
                setShowWarningModal(true);
            } else {
                console.error('Operating cost not found:', activity.operatingCostKey);
            }
        } catch (error) {
            console.error('Error loading operating cost:', error);
        }
    }, []);

    // Proceed to edit modal after warning acknowledgment
    const handleProceedToEdit = useCallback(() => {
        setShowWarningModal(false);
        setShowEditModal(true);
    }, []);

    // Handle operating cost update
    const handleOperatingCostSubmit = useCallback(async (data: {
        categoryKey: string;
        amount: number;
        interval: CostInterval;
        description?: string;
        supplier?: string;
    }) => {
        if (!selectedOperatingCost) return;

        setEditSubmitting(true);
        try {
            await operatingCostsApi.updateCost(
                selectedOperatingCost.buildingKey,
                selectedOperatingCost.cost.key,
                data
            );
            setShowEditModal(false);
            setSelectedOperatingCost(null);
            onOperatingCostUpdated();
        } catch (error) {
            console.error('Error updating operating cost:', error);
            throw error;
        } finally {
            setEditSubmitting(false);
        }
    }, [selectedOperatingCost, onOperatingCostUpdated]);

    // Close modals
    const handleCloseModals = useCallback(() => {
        setShowWarningModal(false);
        setShowEditModal(false);
        setSelectedOperatingCost(null);
    }, []);

    // Initialize Bootstrap tooltips
    useEffect(() => {
        const initializeTooltips = () => {
            const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            return tooltipTriggerList.map((tooltipTriggerEl) => {
                if ((window as any).bootstrap && (window as any).bootstrap.Tooltip) {
                    return new (window as any).bootstrap.Tooltip(tooltipTriggerEl, {
                        container: 'body',
                        boundary: 'viewport'
                    });
                }
                return null;
            }).filter(Boolean);
        };

        // Try to initialize immediately
        let tooltipList = initializeTooltips();
        
        // If Bootstrap isn't available yet, wait a bit and try again
        if (tooltipList.length === 0) {
            const timeoutId = setTimeout(() => {
                tooltipList = initializeTooltips();
            }, 100);
            
            return () => {
                clearTimeout(timeoutId);
                tooltipList.forEach(tooltip => tooltip.dispose());
            };
        }

        // Cleanup tooltips when component unmounts
        return () => {
            tooltipList.forEach(tooltip => tooltip.dispose());
        };
    }, [adjustForInflation]); // Re-initialize when inflation setting changes

    // Set Y-axis label tspan x position to 35 and dy to -40px, and Y-axis tick label tspans x to 95.
    // These fixed positions assume the desktop bar chart (80px left margin, axis title).
    // On phones the chart has a 64px axis and no title, so recharts' own positions are
    // right and this override would push the tick labels across the axis line.
    const isSmallScreen = useIsSmallScreen();
    useEffect(() => {
        if (isSmallScreen) return;
        const setYAxisLabelPosition = () => {
            // Set the "Kostnad (kr)" label
            const labelTspans = document.querySelectorAll<SVGTSpanElement>('.recharts-yAxis .recharts-label tspan');
            labelTspans.forEach((tspan) => {
                if (tspan.textContent === 'Kostnad (kr)') {
                    tspan.setAttribute('x', '35');
                    tspan.setAttribute('dy', '-40px');
                }
            });

            // Set Y-axis tick label tspans (the numbers like "1 000 000")
            const tickTspans = document.querySelectorAll<SVGTSpanElement>('.recharts-yAxis .recharts-cartesian-axis-tick tspan');
            tickTspans.forEach((tspan) => {
                tspan.setAttribute('x', '95');
            });
        };

        // Try immediately
        setYAxisLabelPosition();

        // Also try after a short delay in case chart hasn't rendered yet
        const timeoutId = setTimeout(setYAxisLabelPosition, 100);

        return () => clearTimeout(timeoutId);
    }, [yearlyPlans, isSmallScreen]); // Re-run when yearly plans or the layout change

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Laddar...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="ekonomi-container">
            {/* Main two-column layout */}
            <div className="ekonomi-main-layout">
                {/* Left column: Controls + Bar chart + Yearly plans */}
                <div className="ekonomi-main-column">
                    {/* Controls */}
            <div className="std-box dropshadow" style={{ marginBottom: '20px', padding: '24px' }}>
                {/* First row: Years, Building type, Inflation */}
                <div className="row align-items-center mb-3">
                    <div className="col-md-4">
                        <div className="d-flex align-items-center">
                            <label htmlFor="yearsToShow" className="form-label me-3 mb-0">
                                Visa prognos för:
                            </label>
                            <select
                                id="yearsToShow"
                                className="form-select"
                                value={yearsToShow}
                                onChange={(e) => setYearsToShow(parseInt(e.target.value, 10))}
                                style={{ width: 'auto' }}
                            >
                                <option value={10}>10 år</option>
                                <option value={20}>20 år</option>
                                <option value={30}>30 år</option>
                                <option value={40}>40 år</option>
                                <option value={50}>50 år</option>
                            </select>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <CustomMultiSelect
                            label="Byggnadstyp"
                            options={buildingTypeOptions}
                            value={selectedBuildingTypes}
                            onChange={(value) => setSelectedBuildingTypes(value)}
                            placeholder="Välj byggnadstyper"
                            maxSelectedLabels={2}
                            selectedItemsLabel="{0} byggnadstyper valda"
                        />
                    </div>
                    {/* Right-aligned beside the other controls on desktop; stacked and
                        left-aligned on phones (see .ekonomi-inflation-col in EkonomiPage.css). */}
                    <div className="col-md-4 d-flex flex-column align-items-end ekonomi-inflation-col" style={{ alignSelf: 'flex-start' }}>
                        <label className="form-label ekonomi-inflation-spacer" aria-hidden="true">&nbsp;</label>
                        <div>
                            <label className="ekonomi-toggle">
                                <ToggleSwitch small checked={adjustForInflation} onChange={setAdjustForInflation} />
                                <span>Inflationsjustera</span>
                            </label>
                            <div
                                className="d-flex align-items-center gap-2"
                                style={{
                                    marginTop: '8px',
                                    marginLeft: '38px',
                                    visibility: adjustForInflation ? 'visible' : 'hidden'
                                }}
                            >
                                <NumberInput
                                    className="form-control form-control-sm"
                                    value={inflationRate}
                                    onChange={setInflationRate}
                                    min="0"
                                    max="20"
                                    step="0.5"
                                    style={{ width: '70px' }}
                                />
                                <span style={{ fontSize: '14px' }}>% per år</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Second row: Cost type toggles */}
                <div className="row mb-3">
                    <div className="col-12">
                        <label className="form-label">Kostnads- och intäktstyper</label>
                        <div className="ekonomi-toggles">
                            {ALL_FINANCIAL_CATEGORIES.map(category => (
                                <label key={category} className="ekonomi-toggle">
                                    <ToggleSwitch
                                        small
                                        checked={selectedCostCategories.includes(category)}
                                        onChange={(checked) => {
                                            if (checked) {
                                                setSelectedCostCategories([...selectedCostCategories, category]);
                                            } else {
                                                setSelectedCostCategories(selectedCostCategories.filter(c => c !== category));
                                            }
                                        }}
                                    />
                                    <span
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '50%',
                                            backgroundColor: COST_CATEGORY_COLORS[category],
                                            display: 'inline-block',
                                            flexShrink: 0
                                        }}
                                    />
                                    <span>{COST_CATEGORY_LABELS[category]}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 text-center">
                        <div className="stats-summary justify-content-center">
                            <span className="badge bg-warning">
                                <Coins size={16} className="me-1" />
                                {Math.round(totalCost).toLocaleString('sv-SE')} kr
                            </span>
                        </div>
                    </div>
                </div>
            </div>

                    {/* Bar Chart with Pie Chart */}
                    {filteredYearlyPlans.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <CostProjectionChart
                                yearlyPlans={filteredYearlyPlans}
                                selectedCostCategories={selectedCostCategories}
                                yearsToShow={yearsToShow}
                                adjustForInflation={adjustForInflation}
                            />
                        </div>
                    )}

                    {/* Budget Card - shown here on narrow screens */}
                    <div className="budget-card-mobile">
                        <BudgetCard currentYearCost={currentYearCost} currentYearCostByCategory={currentYearCostByCategory} />
                    </div>

                    {/* Yearly Plans */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {filteredYearlyPlans.map((yearPlan) => {
                    const isExpanded = expandedYears.has(yearPlan.year);
                    const showEmptyBuildings = yearsShowingEmptyBuildings.has(yearPlan.year);
                    return (
                        <div key={yearPlan.year} className="std-box dropshadow">
                            {/* Accordion header: a button inside the heading. Uses its own
                                ekonomi-year-* classes because the generic .year-* classes are
                                also styled by the Förvaltning maintenance pages, and those
                                global rules leaked into these cards. */}
                            <h3 className="ekonomi-year-heading">
                                <button
                                    type="button"
                                    className={`ekonomi-year-header${isExpanded ? ' expanded' : ''}`}
                                    onClick={() => toggleYearExpanded(yearPlan.year)}
                                    aria-expanded={isExpanded}
                                >
                                    <span className="ekonomi-year-label">
                                        {isExpanded
                                            ? <ChevronDown size={20} aria-hidden="true" />
                                            : <ChevronRight size={20} aria-hidden="true" />}
                                        <span className="ekonomi-year-title">{yearPlan.year}</span>
                                    </span>
                                    <span
                                        className={`ekonomi-year-amount${yearPlan.totalCost < 0 ? ' income' : yearPlan.totalCost === 0 ? ' zero' : ''}`}
                                    >
                                        <Coins size={16} aria-hidden="true" />
                                        {Math.round(yearPlan.totalCost).toLocaleString('sv-SE')} kr
                                    </span>
                                </button>
                            </h3>

                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateRows: isExpanded ? '1fr' : '0fr',
                                    transition: 'grid-template-rows 0.25s ease-out'
                                }}
                            >
                                <div style={{ overflow: 'hidden' }}>
                                    {/* The table is wider than the card on phones (and on
                                        narrower desktops), so it scrolls sideways here. */}
                                    <div className="ekonomi-year-table-scroll">
                                        <YearBuildingSummaryTable
                                            activities={yearPlan.activities}
                                            selectedCostCategories={selectedCostCategories}
                                            showEmptyBuildings={showEmptyBuildings}
                                            onToggleShowEmpty={() => toggleShowEmptyBuildings(yearPlan.year)}
                                            allBuildingKeys={allBuildingKeys}
                                            buildingSummaries={buildingSummaries}
                                            onCellClick={handleCellClick}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                </div>
                </div>

                {/* Right column: Budget - shown here on wide screens */}
                <div className="ekonomi-side-column budget-card-desktop">
                    <BudgetCard currentYearCost={currentYearCost} currentYearCostByCategory={currentYearCostByCategory} />
                </div>
            </div>

            {activeBuildingsCount === 0 && (
                <div className="no-plans-section">
                    <div className="alert alert-info">
                        <h4 className="alert-heading">Inga aktiva underhållsplaner</h4>
                        <p className="mb-0">
                            Det finns inga aktiva underhållsplaner att visa kostnader för.
                            Skapa och aktivera underhållsplaner för att se ekonomiska prognoser.
                        </p>
                    </div>
                </div>
            )}

            {/* Warning Modal - shown before editing operating cost */}
            <Modal show={showWarningModal} onHide={handleCloseModals} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        <AlertTriangle size={24} className="text-warning me-2" />
                        Redigera driftkostnad
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-3">
                        <strong>Obs!</strong> Ändringar i denna driftkostnad påverkar alla framtida års prognoser.
                    </Alert>
                    {selectedOperatingCost && (
                        <p>
                            Du redigerar driftkostnaden för <strong>{selectedOperatingCost.buildingName}</strong>.
                            Nya värden kommer att projiceras framåt i alla kommande år.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModals}>
                        Avbryt
                    </Button>
                    <Button variant="primary" onClick={handleProceedToEdit}>
                        Fortsätt till redigering
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Operating Cost Edit Modal */}
            {selectedOperatingCost && (
                <OperatingCostModal
                    show={showEditModal}
                    onHide={handleCloseModals}
                    onSubmit={handleOperatingCostSubmit}
                    cost={selectedOperatingCost.cost}
                    submitting={editSubmitting}
                />
            )}

            {/* Cell Activities Modal */}
            <Modal show={showCellModal} onHide={handleCloseCellModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedCell && (
                            <>
                                <span
                                    style={{
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        backgroundColor: COST_CATEGORY_COLORS[selectedCell.category] || '#ccc',
                                        display: 'inline-block',
                                        marginRight: '10px'
                                    }}
                                />
                                {selectedCell.buildingName}
                            </>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {selectedCell && selectedCell.activities.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Aktivitet</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Komponent</th>
                                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Kostnad</th>
                                    {selectedCell.activities.some(a => a.costCategory === 'operating-cost') && (
                                        <th style={{ padding: '10px 12px', textAlign: 'center', width: '80px' }}>Redigera</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {selectedCell.activities.map((activity, index) => (
                                    <tr
                                        key={`${activity.componentKey}-${activity.activityName}-${index}`}
                                        style={{
                                            borderBottom: '1px solid #e9ecef',
                                            backgroundColor: index % 2 === 0 ? 'transparent' : '#f8f9fa'
                                        }}
                                    >
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '50%',
                                                        backgroundColor: COST_CATEGORY_COLORS[activity.costCategory || ''] || '#ccc',
                                                        flexShrink: 0
                                                    }}
                                                />
                                                {activity.activityName}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 12px', color: '#666' }}>
                                            {activity.componentName}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                            <div>{Math.round(Math.abs(activity.cost)).toLocaleString('sv-SE')} kr</div>
                                            {activity.costCalculation && (
                                                <div style={{ fontSize: '12px', color: '#666' }}>
                                                    {activity.costCalculation}
                                                </div>
                                            )}
                                        </td>
                                        {selectedCell.activities.some(a => a.costCategory === 'operating-cost') && (
                                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                                {activity.costCategory === 'operating-cost' && activity.operatingCostKey && (
                                                    <button
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => {
                                                            handleCloseCellModal();
                                                            handleOperatingCostClick({
                                                                buildingKey: activity.buildingKey,
                                                                buildingName: activity.buildingName,
                                                                operatingCostKey: activity.operatingCostKey
                                                            });
                                                        }}
                                                        title="Redigera driftkostnad"
                                                    >
                                                        <Edit3 size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ borderTop: '2px solid #dee2e6', backgroundColor: '#f1f3f4' }}>
                                    <td colSpan={2} style={{ padding: '12px', fontWeight: 600 }}>
                                        Summa ({selectedCell.activities.length} aktiviteter)
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700 }}>
                                        {Math.round(selectedCell.activities.reduce((sum, a) => sum + a.cost, 0)).toLocaleString('sv-SE')} kr
                                    </td>
                                    {selectedCell.activities.some(a => a.costCategory === 'operating-cost') && (
                                        <td></td>
                                    )}
                                </tr>
                            </tfoot>
                        </table>
                    ) : (
                        <p className="text-muted text-center">Inga aktiviteter att visa</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCellModal}>
                        Stäng
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MaintenanceCostsTab;
